import React, { useState } from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useAllMetrics } from 'features/metrics/useAllMetrics';
import { getMetricValue, getMeasureName } from './claimsTrendMetricsCardHelper';
import { IconTrendingUp } from '@tabler/icons-react';
import { MetricCardErrorState, MetricCardLoadingState, MetricCardNoDataText } from './MetricCardStates';

const MemberCostShareMetricsCard = ({ workBookName, width, height, enablePriorComparison = true }) => {
  const [toggle, setToggle] = useState('Current');

  // Use unified all-metrics VDS hook (single call for all 8 tiles)
  const { data: metricsData, loading, error } = useAllMetrics();

  const handleToggle = (event, newValue) => {
    if (newValue) setToggle(newValue);
  };

  const currentValue = getMetricValue(metricsData, getMeasureName(workBookName, null, 'Current'));
  const priorValue = enablePriorComparison
    ? getMetricValue(metricsData, getMeasureName(workBookName, null, 'Prior'))
    : null;
  const isPriorAvailable = priorValue !== null;
  const activeToggle = isPriorAvailable ? toggle : 'Current';
  const metricValue = activeToggle === 'Prior' ? priorValue : currentValue;
  const extractedValue = metricValue !== null ? metricValue * 100 : null;
  const percentageDifferenceBetweenCurrentAndPrior = isPriorAvailable
    ? getMetricValue(metricsData, '% Diff from Prior')
    : null;

  // Show loading skeleton while data is being fetched
  if (loading) {
    return <MetricCardLoadingState width={width} height={height} borderRadius="26px" />;
  }

  if (error) {
    return <MetricCardErrorState title="Member Cost Share By Plan Year" />;
  }

  if(error || !metricsData || extractedValue === null || extractedValue === undefined) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '176px',
        padding: '24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        alignSelf: 'stretch',
        borderRadius: '16px',
        border: '1px solid var(--Gray---Line-Stroke, #71767A)',
        background: 'var(--white-bi, #FFF)',
      }}
    >
      <Typography
        sx={{
          color: 'var(--Gray-Darkest---Body-Text, #121A22)',
          fontFamily: 'Noto Sans',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: '150%',
        }}
      >
        Member Cost Share By Plan Year
      </Typography>
      {isPriorAvailable && <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          alignSelf: 'stretch'
        }}
      >
        <ToggleButtonGroup
        value={activeToggle}
        exclusive
        onChange={handleToggle}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '8px',
            bgcolor: 'transparent',
            px: 0,
            py: 0,
            fontSize: 14,
            fontWeight: 400,
            fontFamily: 'Noto Sans',
            lineHeight: '150%',
            textTransform: 'none',
            color: '#2F54A5',
            '&:hover': { bgcolor: 'transparent' },
            '&:hover:not(.Mui-selected)': { color: '#068DF1' },
            '&.Mui-selected': {
              bgcolor: 'transparent',
              fontWeight: 600,
              textDecoration: 'underline',
              textDecorationSkipInk: 'none'
            }
          },
          '& .MuiToggleButton-root + .MuiToggleButton-root': {
            marginLeft: '12px'
          }
        }}
      >
        <ToggleButton value="Current">Current</ToggleButton>
        <ToggleButton value="Prior">Prior</ToggleButton>
      </ToggleButtonGroup>
      </Box>}
      {extractedValue != null ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '16px',
            alignSelf: 'stretch',
            marginTop: 'auto',
          }}
        >
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 400,
              fontStyle: 'normal',
              fontFamily: 'Noto Sans',
              color: '#121A22',
              lineHeight: '150%',
              alignItems: 'center',
            }}
          >
            {`${parseFloat(extractedValue).toFixed(2)}%`}
          </Typography>
          {activeToggle === 'Current' && percentageDifferenceBetweenCurrentAndPrior !== null && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: 'auto',
                  columnGap: '4px',
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Noto Sans',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    color: '#000',
                    lineHeight: '21px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${parseFloat(percentageDifferenceBetweenCurrentAndPrior).toFixed(2)}% vs prior`}
                </Typography>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconTrendingUp size={16} color="#007A14" />
                </Box>
              </Box>
          )}
        </Box>
      ) : (
        <MetricCardNoDataText />
      )}
    </Box>
  );
};

export default MemberCostShareMetricsCard;
